import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

interface Article { id: string; title: string; blurb: string }
interface Section { icon: string; title: string; tag: string; articles: Article[] }

const DOC_SECTIONS: Section[] = [
  {
    icon: '◆', title: 'The Grid', tag: 'GR',
    articles: [
      { id: 'quickstart',    title: 'Quickstart',                blurb: 'cat data.json | instadash push — your first live grid in under a minute.' },
      { id: 'one-line',      title: 'The One-Line Push',         blurb: 'Pipe from Python, Node, Bash, or any tool that prints to stdout.' },
      { id: 'streaming',     title: 'Large File Streaming',      blurb: 'Push 2 GB+ files directly to R2 without hitting the Worker size limit.' },
      { id: 'options',       title: 'Configuration Reference',   blurb: '--name, --title, --tags, --public, --private — the full push-time flag surface.' },
    ],
  },
  {
    icon: '○', title: 'Privacy & Data', tag: 'PD',
    articles: [
      { id: 'public-private',title: 'Visibility',                blurb: 'Grids are private by default. Go public with a flag, and control embed access per plan.' },
      { id: 'lineage',       title: 'Data Lineage',              blurb: 'Source tracking on every version — cli, api, mcp. SHA-256 for integrity.' },
      { id: 'time-machine',  title: 'Atomic Versioning',         blurb: 'Slide back in time to see data evolution. Every sync is a snapshot.' },
      { id: 'r2-storage',    title: 'R2 Storage & Pricing',      blurb: 'Immutable JSONL on Cloudflare R2. $0 egress. Plan-based retention.' },
    ],
  },
  {
    icon: '◈', title: 'Agent Integration', tag: 'AG',
    articles: [
      { id: 'mcp',           title: 'MCP Integration',           blurb: 'Add instadash-mcp to Claude, Cursor, and Windsurf for native push and read tools.' },
      { id: 'system-prompt', title: 'System Prompting',          blurb: 'Every grid ships llms.txt and llms.md — paste them straight into your system prompt.' },
      { id: 'auth',          title: 'API Authentication',        blurb: 'Bearer API keys, scoping, and rotation. Rate limits by plan.' },
      { id: 'webhooks',      title: 'Webhooks',                  blurb: 'Get notified on grid.pushed. HMAC-signed outbound events, builder+ only.' },
    ],
  },
  {
    icon: '◇', title: 'Mesh & Discovery', tag: 'MD',
    articles: [
      { id: 'mesh-protocol', title: 'The Mesh Protocol',         blurb: 'How agents search public grids via /api/mesh/search.' },
      { id: 'mesh-memory',   title: 'The Grid as Agent Memory',  blurb: 'Push data in one session, query it back in another. Best practices for persistent agent memory.' },
      { id: 'mag',           title: 'Mesh-Augmented Generation', blurb: 'RAG retrieves from documents. The mesh retrieves from live, structured grids with lineage.' },
      { id: 'cross-grid',    title: 'Cross-Project Workflows',   blurb: "Trigger an agent based on updates in someone else's public grid." },
    ],
  },
]

export function DocsIndex() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  return (
    <div style={{ padding: '40px 0 80px' }}>
      <div className="container">
        <div className="row" style={{ marginBottom: 32, gap: 14 }}>
          <span className="chip mono" style={{ fontSize: 11 }}>docs · v1.0</span>
        </div>
        <h1 className="h-display" style={{ fontSize: 'clamp(40px, 5vw, 68px)' }}>Docs.</h1>
        <p className="lede" style={{ marginTop: 16, marginBottom: 36, maxWidth: 600 }}>
          One push. Two surfaces. A <strong style={{ color: 'var(--accent)' }}>live grid</strong> for humans to review — and a queryable API, embeddable view, and LLM context file for agents to reuse.
          <span style={{ display: 'block', marginTop: 8, fontSize: 14, color: 'var(--fg-3)' }}>No database. No frontend. No schema setup.</span>
        </p>

        <div style={{ position: 'relative', marginBottom: 40, maxWidth: 540 }}>
          <span className="mono" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--accent)', fontSize: 14 }}>⌕</span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder='ask anything — "stream a 4 GB ndjson"…'
            className="mono"
            style={{
              width: '100%', padding: '12px 14px 12px 36px', fontSize: 14,
              background: 'var(--bg-1)', border: '1px solid var(--line)',
              borderRadius: 6, color: 'var(--fg)', outline: 'none',
            }}
          />
          <span className="mono" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-3)', fontSize: 11, padding: '3px 8px', border: '1px solid var(--line)', borderRadius: 3 }}>⌘ /</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {DOC_SECTIONS.map(sec => (
            <div key={sec.title} className="card">
              <div className="row" style={{ padding: '14px 22px', borderBottom: '1px solid var(--line-soft)' }}>
                <span className="mono" style={{ fontSize: 11, color: 'var(--accent)', letterSpacing: '0.1em' }}>{sec.tag}</span>
                <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 14 }}>{sec.title}</span>
                <span className="spacer"></span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>{sec.articles.length} articles</span>
              </div>
              <div>
                {sec.articles
                  .filter(a => !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.blurb.toLowerCase().includes(search.toLowerCase()))
                  .map(a => (
                  <div key={a.id} onClick={() => navigate(`/docs/${a.id}`)}
                    style={{
                      padding: '14px 22px',
                      borderBottom: '1px solid var(--line-soft)',
                      cursor: 'pointer', transition: 'background .12s',
                      display: 'flex', gap: 16, alignItems: 'center',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 3 }}>{a.title}</div>
                      <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>{a.blurb}</div>
                    </div>
                    <span style={{ color: 'var(--fg-3)', fontSize: 14 }}>›</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 48, padding: 24, border: '1px solid var(--line-soft)', borderRadius: 8, display: 'flex', gap: 24, alignItems: 'center', background: 'var(--bg-1)' }}>
          <div style={{ flex: 1 }}>
            <div className="eyebrow" style={{ marginBottom: 6 }}>need help?</div>
            <div className="h3" style={{ marginBottom: 4 }}>Talk to a human or to the agent.</div>
            <p className="muted" style={{ fontSize: 13 }}>Discord for community. Email for paying customers. MCP support for your AI itself.</p>
          </div>
          <div className="row gap-8">
            <button className="btn btn-ghost btn-mono">/discord</button>
            <button className="btn btn-ghost btn-mono">support@</button>
            <button className="btn btn-primary btn-mono">live chat →</button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function DocsArticle() {
  const navigate = useNavigate()
  const { articleId } = useParams<{ articleId: string }>()
  const all = DOC_SECTIONS.flatMap(s => s.articles.map(a => ({ ...a, section: s.title, tag: s.tag })))
  const article = all.find(a => a.id === articleId) || all[0]

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', minHeight: 'calc(100vh - 53px)' }}>
      <aside style={{ borderRight: '1px solid var(--line-soft)', padding: '28px 18px', position: 'sticky', top: 53, height: 'calc(100vh - 53px)', overflow: 'auto' }}>
        <div className="mono" style={{ fontSize: 11, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14, padding: '0 8px' }}>docs</div>
        {DOC_SECTIONS.map(sec => (
          <div key={sec.title} style={{ marginBottom: 18 }}>
            <div className="mono" style={{ fontSize: 11, color: 'var(--fg-2)', padding: '4px 8px', fontWeight: 500 }}>{sec.title}</div>
            <div>
              {sec.articles.map(a => (
                <div key={a.id} onClick={() => navigate(`/docs/${a.id}`)}
                  style={{
                    padding: '5px 8px', borderRadius: 4, fontSize: 12.5, cursor: 'pointer',
                    color: a.id === article.id ? 'var(--accent)' : 'var(--fg-1)',
                    background: a.id === article.id ? 'var(--accent-soft)' : 'transparent',
                    transition: 'background .12s',
                  }}>
                  {a.title}
                </div>
              ))}
            </div>
          </div>
        ))}
      </aside>

      <main className="docs-body" style={{ padding: '40px 56px 80px', maxWidth: 760, width: '100%' }}>
        <div className="row mono" style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 18, gap: 8 }}>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/docs')}>docs</span>
          <span>/</span>
          <span>{article.section}</span>
          <span>/</span>
          <span style={{ color: 'var(--fg-1)' }}>{article.title}</span>
        </div>
        <h1 className="h1" style={{ marginBottom: 14 }}>{article.title}</h1>
        <p style={{ fontSize: 16, color: 'var(--fg-1)', lineHeight: 1.65, marginBottom: 40, maxWidth: '52ch' }}>{article.blurb}</p>

        <ArticleBody id={article.id} />

        <div className="row" style={{ marginTop: 56, paddingTop: 24, borderTop: '1px solid var(--line-soft)', gap: 12 }}>
          <button className="btn btn-ghost btn-mono" onClick={() => navigate('/docs')}>← all docs</button>
          <span className="spacer"></span>
          <a
            href="https://github.com/instadashio/instadash/edit/main/dashboard/src/pages/Docs.tsx"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-mono"
          >
            edit on github
          </a>
        </div>
      </main>
    </div>
  )
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="h2" style={{ fontSize: 20, marginTop: 40, marginBottom: 12, letterSpacing: '-0.015em', color: 'var(--accent)' }}>{children}</h2>
}
function P({ children }: { children: ReactNode }) {
  return <p style={{ fontSize: 15, color: 'var(--fg)', lineHeight: 1.8, marginBottom: 16 }}>{children}</p>
}
function CodeBlock({ children, lang }: { children: string; lang: string }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(children).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--line-soft)', borderRadius: 6, marginBottom: 20, overflow: 'hidden' }}>
      <div className="row" style={{ padding: '8px 14px', borderBottom: '1px solid var(--line-soft)', justifyContent: 'space-between' }}>
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{lang}</span>
        <span
          className="mono"
          onClick={copy}
          style={{ fontSize: 10, color: copied ? 'var(--accent)' : 'var(--fg-3)', cursor: 'pointer', userSelect: 'none', transition: 'color .15s' }}
        >
          {copied ? 'copied!' : 'copy'}
        </span>
      </div>
      <pre className="mono" style={{ padding: '16px 18px', fontSize: 12.5, lineHeight: 1.8, color: 'var(--fg-1)', overflowX: 'auto', whiteSpace: 'pre' }}>{children}</pre>
    </div>
  )
}
function Callout({ tag = 'note', children }: { tag?: string; children: ReactNode }) {
  return (
    <div style={{ padding: '12px 16px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, marginBottom: 20, display: 'flex', gap: 12 }}>
      <span className="mono" style={{ fontSize: 11, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0, paddingTop: 2 }}>{tag}</span>
      <span style={{ fontSize: 14, color: 'var(--fg)', lineHeight: 1.7 }}>{children}</span>
    </div>
  )
}

function ArticleBody({ id }: { id: string }) {
  if (id === 'quickstart') {
    return (
      <>
        <H2>1. Install the CLI</H2>
        <P>Instadash ships as a single npm package. One command — <code>instadash push</code> — creates a live grid from any JSON, JSONL, or CSV source.</P>
        <CodeBlock lang="bash">{`npm install -g instadash
# or use it via npx without installing:
npx instadash push --key sk_… --name first --file data.jsonl`}</CodeBlock>

        <H2>2. Pipe your first file</H2>
        <P>Anything that produces JSON or JSONL is fair game.</P>
        <CodeBlock lang="bash">{`npx instadash push --key sk_… --name first-grid --file orders.jsonl
# → grid created · 12,408 rows · private
# → schema inferred · 8 cols
# → https://instadash.io/<your-handle>/first-grid`}</CodeBlock>

        <H2>3. Read it back</H2>
        <P>Your grid has a webpage, a JSON API, and an LLM-readable surface.</P>
        <CodeBlock lang="bash">{`# Webpage
open https://instadash.io/<handle>/first-grid

# Paginated rows API
curl https://instadash.io/<handle>/first-grid/rows

# LLM-readable
curl https://instadash.io/<handle>/first-grid/llms.md`}</CodeBlock>

        <Callout tag="note">
          Private by default. Pass <code>--public</code> to register your grid on the mesh and make it visible to other agents.
        </Callout>

        <H2>What just happened</H2>
        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 0, marginTop: 14 }}>
          {[
            ['POST',     'Your stdout streamed to /ingest as chunked JSONL.'],
            ['STORE',    'Data landed in R2 at grids/<handle>/<slug>/v<n>.jsonl — immutable, $0 egress.'],
            ['REGISTER', 'Private by default. Pass --public to register on the mesh so agents can search and cite this grid.'],
            ['VIEW',     'A live grid URL was minted and returned in the response body.'],
          ].map(([k, v]) => (
            <li key={k} className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--accent)', width: 80, paddingTop: 3 }}>{k}</span>
              <span style={{ fontSize: 14, color: 'var(--fg-1)', lineHeight: 1.6 }}>{v}</span>
            </li>
          ))}
        </ul>
      </>
    )
  }

  if (id === 'mcp') {
    return (
      <>
        <H2>Connecting via MCP</H2>
        <P>The Model Context Protocol gives any LLM tool a typed handshake to your data layer. Drop the Instadash MCP server into your editor's config and your agent gains <code>push</code>, <code>read</code>, <code>list</code>, <code>versions</code>, and <code>instamesh_search</code> tools.</P>
        <CodeBlock lang="json">{`{
  "mcpServers": {
    "instadash": {
      "command": "npx",
      "args": ["-y", "instadash-mcp"],
      "env": { "INSTADASH_KEY": "sk_live_…" }
    }
  }
}`}</CodeBlock>
        <H2>Hosts we test against</H2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 12 }}>
          {[
            ['Claude Desktop', '✓ supported', 'var(--lime)'],
            ['Cursor',         '✓ supported', 'var(--lime)'],
            ['Windsurf',       '✓ supported', 'var(--lime)'],
            ['Zed',            'beta',         'var(--warn)'],
            ['Continue.dev',   'beta',         'var(--warn)'],
            ['VS Code Copilot','planned',     'var(--fg-3)'],
          ].map(([host, status, c]) => (
            <div key={host} className="card" style={{ padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{host}</div>
              <div className="mono" style={{ fontSize: 11, color: c, marginTop: 4 }}>{status}</div>
            </div>
          ))}
        </div>
        <H2>Tools your agent gets</H2>
        <CodeBlock lang="text">{`instadash_list()                        // all grids in workspace
instadash_get_schema(grid)              // column schema + sample row
instadash_read(grid, offset, limit)     // paginated rows
instadash_push(name, rows, title?,      // append rows; optional metadata
               description?, tags?)
instadash_list_versions(grid)           // version history
instamesh_search(q, k?)                 // search across public mesh`}</CodeBlock>
      </>
    )
  }

  if (id === 'time-machine') {
    return (
      <>
        <H2>Every sync is a snapshot</H2>
        <P>Time Machine isn't a feature — it's the storage model. Each push writes a fresh, immutable JSONL blob to R2 at <code>grids/&lt;handle&gt;/&lt;slug&gt;/v&lt;n&gt;.jsonl</code>. The "current" view is just a pointer to the latest version.</P>
        <CodeBlock lang="bash">{`# List versions
npx instadash versions q2-revenue --key sk_…

# v24  2026-04-30T22:14:08Z  +1,240 rows  (cli)
# v23  2026-04-30T21:01:55Z  +    8 rows  (mcp)
# v22  2026-04-30T19:33:14Z  +  220 rows  (api)`}</CodeBlock>
        <Callout tag="cost">
          R2 has $0 egress. Holding 10,000 versions costs you the same in reads as holding one.
        </Callout>
      </>
    )
  }

  if (id === 'mesh-protocol') {
    return (
      <>
        <H2>What is the Mesh?</H2>
        <P>The mesh is a global registry sitting on top of every public Instadash grid. It's powered by D1 + AI Search and lets agents discover grids by natural-language query — no upfront indexing required.</P>
        <P>Every public push registers a row in the mesh with title, description, tags, columns, and row count. Agents query <code>/api/mesh/search</code> with a natural-language query and get back a list of matching grids with attribution.</P>

        <H2>Browsing the registry</H2>
        <CodeBlock lang="bash">{`curl https://instadash.io/api/mesh/grids?limit=20`}</CodeBlock>

        <H2>Search</H2>
        <CodeBlock lang="bash">{`curl https://instadash.io/api/mesh/search?q=AU+housing+prices`}</CodeBlock>

        <Callout tag="status">
          AI Search is live for public grids. Results include attribution, source URLs, and the version timestamp agents should cite.
        </Callout>
      </>
    )
  }

  if (id === 'auth') {
    return (
      <>
        <H2>Minting a key</H2>
        <P>All CLI and MCP access uses a Bearer API key. Create one via the CLI login flow (it saves to <code>~/.config/instadash/config.json</code>) or via the API.</P>
        <CodeBlock lang="bash">{`# Interactive browser login — saves key automatically
instadash login

# Or mint one directly via API
curl -X POST https://instadash.io/api/keys \\
  -H "Cookie: <session-cookie>" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"my-agent"}'
# → { "key": "sk_live_…", "name": "my-agent", "id": "…" }`}</CodeBlock>

        <H2>Using a key</H2>
        <P>Pass the key as a Bearer token on any <code>/ingest</code>, <code>/api/</code>, or <code>/:handle/:slug/*</code> endpoint.</P>
        <CodeBlock lang="bash">{`# One-off
curl https://instadash.io/api/workspace \\
  -H "Authorization: Bearer sk_live_…"

# Persistent env var (CLI picks this up automatically)
export INSTADASH_KEY=sk_live_…
instadash push --file data.jsonl --name my-grid

# MCP config
{ "env": { "INSTADASH_KEY": "sk_live_…" } }`}</CodeBlock>

        <H2>Listing and revoking</H2>
        <CodeBlock lang="bash">{`# List active keys
curl https://instadash.io/api/keys \\
  -H "Authorization: Bearer sk_live_…"

# Revoke by ID
curl -X DELETE https://instadash.io/api/keys/<id> \\
  -H "Authorization: Bearer sk_live_…"`}</CodeBlock>

        <H2>Rate limits</H2>
        <P>Ingest rate limits apply per account across all three push paths (<code>/ingest</code>, <code>/ingest/presign</code>, <code>/ingest/complete</code>). The window resets every hour.</P>
        <div style={{ overflowX: 'auto', marginBottom: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {['Plan', 'Pushes / hr', 'Max rows / push', 'Max file', 'Storage'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--fg-2)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['Free',    '10',  '1,000',     '10 MB',  '100 MB'],
                ['Builder', '60',  '100,000',   '200 MB', '100 GB'],
                ['Agent',   '300', '5,000,000', '2 GB',   '1 TB'],
              ].map(([plan, rph, rows, file, storage]) => (
                <tr key={plan} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{plan}</td>
                  <td className="mono" style={{ padding: '8px 12px', color: 'var(--accent)' }}>{rph}</td>
                  <td className="mono" style={{ padding: '8px 12px' }}>{rows}</td>
                  <td className="mono" style={{ padding: '8px 12px' }}>{file}</td>
                  <td className="mono" style={{ padding: '8px 12px' }}>{storage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Callout tag="note">Hitting a limit returns HTTP 429. The CLI surfaces this as a readable message and exits with code 1.</Callout>
      </>
    )
  }

  if (id === 'public-private') {
    return (
      <>
        <H2>Private by default</H2>
        <P>Every grid starts <strong>private</strong>. A private grid is only accessible with your API key — it won't appear in mesh search results and can't be read without authentication.</P>
        <CodeBlock lang="bash">{`# Private (default — no flag needed)
instadash push --file data.jsonl --name internal-q2

# Explicitly private
instadash push --file data.jsonl --name internal-q2 --private

# Public — visible on the mesh, readable without a key
instadash push --file data.jsonl --name sales-q2 --public`}</CodeBlock>
        <Callout tag="note">All plans support private grids. Public grids are available on all plans too — just pass <code>--public</code>.</Callout>

        <H2>Going public</H2>
        <P>Pushing with <code>--public</code> registers the grid on the mesh and makes it readable without authentication. Any agent with the URL — or that finds it via mesh search — can read the rows.</P>
        <CodeBlock lang="bash">{`instadash push --file data.jsonl --name sales-q2 --public
# ✓ live at instadash.io/<handle>/sales-q2
# ✓ registered on mesh · searchable by agents`}</CodeBlock>

        <H2>Toggling visibility after the fact</H2>
        <P>Change visibility without re-uploading data. Making a grid private removes it from mesh search results immediately.</P>
        <CodeBlock lang="bash">{`instadash visibility sales-q2 private
instadash visibility sales-q2 public

# Or via API
curl -X PATCH https://instadash.io/api/grids/sales-q2/visibility \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{"visibility":"public"}'`}</CodeBlock>

        <H2>Embed access</H2>
        <P>The <code>/meta</code> endpoint returns an <code>allow_embed</code> flag based on the grid owner's plan. Free plan grids return <code>false</code> — the embed view shows an upgrade prompt instead of rendering the grid.</P>
        <CodeBlock lang="bash">{`curl https://instadash.io/raihan/sales-q2/meta
# Free plan:    { …, "allow_embed": false }
# Builder plan: { …, "allow_embed": true  }`}</CodeBlock>
        <CodeBlock lang="html">{`<iframe
  src="https://instadash.io/raihan/sales-q2?embed=true"
  width="100%"
  height="600"
  style="border:none"
></iframe>`}</CodeBlock>
        <Callout tag="plan">Embeddable grids require the Builder plan or above.</Callout>
      </>
    )
  }

  if (id === 'webhooks') {
    return (
      <>
        <H2>What fires</H2>
        <P>Every successful ingest fires a <code>grid.pushed</code> event to all active webhooks on the account. The payload includes the handle, slug, new version, row count, and visibility.</P>
        <CodeBlock lang="json">{`{
  "event": "grid.pushed",
  "grid": {
    "handle":     "raihan",
    "slug":       "sales-q2",
    "version":    4,
    "rows":       12408,
    "visibility": "public"
  },
  "timestamp": "2026-05-07T09:14:22.000Z"
}`}</CodeBlock>

        <H2>Registering a webhook</H2>
        <CodeBlock lang="bash">{`curl -X POST https://instadash.io/api/webhooks \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name":   "my-listener",
    "url":    "https://your-server.com/hooks/instadash",
    "secret": "choose-your-own-or-omit-for-auto"
  }'
# → { "id": "…", "secret": "sha256-signing-secret", "events": ["grid.pushed"], … }`}</CodeBlock>
        <Callout tag="note">If you omit <code>secret</code>, a UUID is generated and returned once. Store it — it is not shown again.</Callout>

        <H2>Verifying signatures</H2>
        <P>Every delivery includes an <code>X-Instadash-Signature</code> header: <code>sha256=</code> followed by the HMAC-SHA256 hex digest of the raw request body, keyed by your secret.</P>
        <CodeBlock lang="js">{`import { createHmac } from 'node:crypto'

function verify(rawBody, secret, header) {
  const expected = 'sha256=' +
    createHmac('sha256', secret).update(rawBody).digest('hex')
  return expected === header
}

// Express example
app.post('/hooks/instadash', (req, res) => {
  if (!verify(req.rawBody, process.env.WEBHOOK_SECRET,
              req.headers['x-instadash-signature'])) {
    return res.sendStatus(401)
  }
  const { event, grid } = req.body
  console.log(event, grid.handle, grid.slug, 'v' + grid.version)
  res.sendStatus(200)
})`}</CodeBlock>

        <H2>Listing and deleting</H2>
        <CodeBlock lang="bash">{`# List
curl https://instadash.io/api/webhooks \\
  -H "Authorization: Bearer sk_live_…"

# Delete
curl -X DELETE https://instadash.io/api/webhooks/<id> \\
  -H "Authorization: Bearer sk_live_…"`}</CodeBlock>
        <Callout tag="plan">Webhooks require the Builder plan or above. The registration endpoint returns HTTP 403 on free accounts.</Callout>
      </>
    )
  }

  if (id === 'one-line') {
    return (
      <>
        <H2>Pipe anything</H2>
        <P>The CLI reads from stdin, a <code>--file</code> path, or a <code>--url</code>. If the data is JSON or JSONL, schema is inferred automatically. If it's CSV, pass <code>--csv</code> and headers become column names.</P>
        <CodeBlock lang="bash">{`# From a file
instadash push --key sk_… --name logs --file app.jsonl

# From stdin — pipe from any command
cat orders.json | instadash push --key sk_… --name orders

# From a URL — fetches and ingests directly
instadash push --key sk_… --name census --url https://example.com/data.jsonl`}</CodeBlock>

        <H2>Extracting nested JSON</H2>
        <P>Use <code>--path</code> to pluck a nested array out of a JSON response before Instadash sees it. This avoids having to pre-process every API response yourself.</P>
        <CodeBlock lang="bash">{`# API returns { "meta": {…}, "results": […] }
curl https://api.example.com/data | \\
  instadash push --key sk_… --name results --path results`}</CodeBlock>

        <H2>CSV auto-parse</H2>
        <CodeBlock lang="bash">{`# Comma-separated, first row = headers
instadash push --key sk_… --name sales --file report.csv --csv

# Tab-separated
instadash push --key sk_… --name sales --file report.tsv --csv --delimiter tab`}</CodeBlock>

        <H2>Common pipe patterns</H2>
        <CodeBlock lang="bash">{`# Python
python analysis.py | instadash push --key sk_… --name analysis

# Node
node export.js | instadash push --key sk_… --name export

# jq transformation
cat raw.json | jq '.items[] | {id, name, value}' | \\
  instadash push --key sk_… --name transformed

# psql query result as CSV
psql -U user -d db -c "COPY (SELECT …) TO STDOUT WITH CSV HEADER" | \\
  instadash push --key sk_… --name query --csv`}</CodeBlock>

        <Callout tag="note">
          The CLI streams from stdin line-by-line. It never buffers the full file in memory — you can pipe terabyte-scale JSONL through it and it won't OOM. For files over 200 MB, the CLI automatically switches to the presigned URL flow.
        </Callout>
      </>
    )
  }

  if (id === 'streaming') {
    return (
      <>
        <H2>Why a two-step upload</H2>
        <P>The <code>/ingest</code> endpoint is great for small to medium files, but Cloudflare Workers cap request bodies at 100 MB. For files up to 2 GB (Agent plan), the CLI switches to a presigned-URL flow that goes directly to R2 — bypassing the Worker entirely.</P>

        <H2>The three steps</H2>
        <CodeBlock lang="text">{`1. POST /ingest/presign       →  get a time-limited R2 PUT URL
2. PUT  <presign-url>         →  upload directly to R2
3. POST /ingest/complete      →  register the version, fire webhooks`}</CodeBlock>

        <CodeBlock lang="bash">{`# Step 1 — request a presigned URL
curl -X POST https://instadash.io/ingest/presign \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{"slug":"big-export","rows":5000000,"bytes":1073741824}'
# → { "upload_url": "https://r2.cloudflarestorage.com/…", "key": "grids/…/v42.jsonl" }

# Step 2 — PUT directly to R2
curl -X PUT "<upload_url>" \\
  -H "Content-Type: application/x-ndjson" \\
  --data-binary @big-export.jsonl

# Step 3 — complete the ingest
curl -X POST https://instadash.io/ingest/complete \\
  -H "Authorization: Bearer sk_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{"key":"grids/…/v42.jsonl","slug":"big-export","public":true}'`}</CodeBlock>

        <H2>The CLI handles this automatically</H2>
        <P>You don't need to implement the three steps yourself. The CLI checks file size and switches to presign mode automatically when the file exceeds 100 MB.</P>
        <CodeBlock lang="bash">{`# Works the same regardless of file size
instadash push --key sk_… --name big-export --file export.jsonl
# → file is 1.2 GB — using presigned upload
# → uploading to R2…  ██████████████░░░░░░ 70%
# → finalizing…
# → https://instadash.io/<handle>/big-export`}</CodeBlock>

        <H2>Plan limits</H2>
        <div style={{ overflowX: 'auto', marginBottom: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {['Plan', 'Max file', 'Max rows / push'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--fg-2)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['Free',    '10 MB',  '1,000'],
                ['Builder', '200 MB', '100,000'],
                ['Agent',   '2 GB',   '5,000,000'],
              ].map(([plan, file, rows]) => (
                <tr key={plan} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{plan}</td>
                  <td className="mono" style={{ padding: '8px 12px', color: 'var(--accent)' }}>{file}</td>
                  <td className="mono" style={{ padding: '8px 12px' }}>{rows}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Callout tag="note">Presigned URLs expire after 15 minutes. If your upload takes longer, re-request a URL from <code>/ingest/presign</code>.</Callout>
      </>
    )
  }

  if (id === 'options') {
    return (
      <>
        <H2>Full push flags</H2>
        <P>Every flag below can be passed to <code>instadash push</code>. Required flags are <code>--key</code> (or the <code>INSTADASH_KEY</code> env var) and at least one data source (<code>--file</code>, <code>--url</code>, or stdin).</P>
        <div style={{ overflowX: 'auto', marginBottom: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {['Flag', 'Default', 'Description'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--fg-2)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['--key, -k',         'INSTADASH_KEY env',  'API key. Overrides env var.'],
                ['--name, -n',        '(required)',          'Grid slug — URL-safe, e.g. sales-q2'],
                ['--title',           'same as --name',      'Human-readable title shown in the UI'],
                ['--description, -d', '""',                  'Freeform description indexed for mesh search'],
                ['--tags',            '[]',                  'Comma-separated tags: --tags "sales,q2,2026"'],
                ['--file, -f',        'stdin',               'Path to local file (.jsonl, .json, .csv)'],
                ['--url',             '—',                   'Remote URL to fetch and ingest'],
                ['--path',            '—',                   'Dot-path to extract from JSON: --path results.items'],
                ['--csv',             'false',               'Parse as CSV. First row = headers.'],
                ['--delimiter',       'comma',               'CSV delimiter: comma | tab | semicolon'],
                ['--public',          'true',                'Publish to mesh (default behavior)'],
                ['--private',         'false',               'Scope to workspace. Builder+ required.'],
              ].map(([flag, def, desc]) => (
                <tr key={flag} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td className="mono" style={{ padding: '8px 12px', color: 'var(--accent)', whiteSpace: 'nowrap' }}>{flag}</td>
                  <td className="mono" style={{ padding: '8px 12px', color: 'var(--fg-3)', fontSize: 12 }}>{def}</td>
                  <td style={{ padding: '8px 12px' }}>{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <H2>Example — fully specified push</H2>
        <CodeBlock lang="bash">{`instadash push \\
  --key sk_live_… \\
  --name q2-revenue \\
  --title "Q2 2026 Revenue" \\
  --description "Consolidated revenue across all regions, Q2 2026" \\
  --tags "revenue,q2,2026,finance" \\
  --file ./revenue.jsonl \\
  --public`}</CodeBlock>

        <H2>Env var shorthand</H2>
        <CodeBlock lang="bash">{`export INSTADASH_KEY=sk_live_…
# --key can now be omitted from every command
instadash push --name q2-revenue --file revenue.jsonl`}</CodeBlock>
        <Callout tag="note">The MCP server reads <code>INSTADASH_KEY</code> from its <code>env</code> block — the same variable, no duplication needed.</Callout>
      </>
    )
  }

  if (id === 'lineage') {
    return (
      <>
        <H2>Every row knows where it came from</H2>
        <P>Each push is stored as a <strong>VersionEntry</strong> — an immutable record that captures who pushed, when, how many rows, and which code path created it.</P>
        <CodeBlock lang="json">{`{
  "v":         42,
  "timestamp": "2026-05-07T09:14:22.000Z",
  "rows":      12408,
  "r2_key":    "grids/raihan/sales-q2/v42.jsonl",
  "source":    "cli",
  "sha256":    "e3b0c44298fc1c14…"
}`}</CodeBlock>

        <H2>Source values</H2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 12, marginBottom: 20 }}>
          {[
            ['cli',     'instadash push from a terminal or CI script'],
            ['api',     'Direct HTTP POST to /ingest'],
            ['mcp',     'instadash_push tool call via MCP'],
            ['webhook', 'Ingest triggered by an inbound webhook (planned)'],
          ].map(([source, desc]) => (
            <div key={source} className="card" style={{ padding: 14 }}>
              <div className="mono" style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 4 }}>{source}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-1)', lineHeight: 1.5 }}>{desc}</div>
            </div>
          ))}
        </div>

        <H2>SHA-256 integrity</H2>
        <P>The <code>sha256</code> field is the hex digest of the raw JSONL blob before it was stored. You can verify data integrity at any point — re-hash the R2 object and compare.</P>
        <CodeBlock lang="bash">{`# Fetch a specific version and verify
curl https://instadash.io/<handle>/sales-q2/versions
# → [..., { "v": 42, "sha256": "e3b0c44…", … }]

# Check locally
shasum -a 256 ./sales-q2-v42.jsonl`}</CodeBlock>

        <H2>Lineage in LLM surfaces</H2>
        <P>The <code>/llms.md</code> endpoint includes an attribution block at the bottom with version, source, timestamp, and sha256 — so any LLM reading that surface knows exactly how fresh the data is and who pushed it.</P>
        <CodeBlock lang="bash">{`curl https://instadash.io/raihan/sales-q2/llms.md
# → | id | region | amount |
# → | …  | …      | …      |
# → ---
# → attribution: { "v": 42, "source": "cli", "timestamp": "2026-05-07…", "sha256": "e3b0c44…" }`}</CodeBlock>
      </>
    )
  }

  if (id === 'r2-storage') {
    return (
      <>
        <H2>How data is stored</H2>
        <P>Every push writes an immutable JSONL blob to Cloudflare R2. Nothing is overwritten — each version lives at its own key forever, until it falls outside your retention window.</P>
        <CodeBlock lang="text">{`grids/<handle>/<slug>/v1.jsonl    ← never mutated
grids/<handle>/<slug>/v2.jsonl
grids/<handle>/<slug>/v3.jsonl    ← "current" pointer`}</CodeBlock>

        <H2>$0 egress</H2>
        <P>Cloudflare R2 has no egress fees. Reading a grid — whether it's your agent pulling 5 million rows or the embed rendering a table — costs the same in egress as doing nothing. You only pay for storage and ingest operations.</P>

        <H2>Retention and storage by plan</H2>
        <div style={{ overflowX: 'auto', marginBottom: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {['Plan', 'Retention', 'Storage limit'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: 'var(--fg-2)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['Free',    '7 days',   '100 MB'],
                ['Builder', '30 days',  '100 GB'],
                ['Agent',   '365 days', '1 TB'],
              ].map(([plan, ret, storage]) => (
                <tr key={plan} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{plan}</td>
                  <td className="mono" style={{ padding: '8px 12px', color: 'var(--accent)' }}>{ret}</td>
                  <td className="mono" style={{ padding: '8px 12px' }}>{storage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Callout tag="note">
          Retention applies to the JSONL blob in R2. The grid metadata (title, schema, version list) persists in D1 after a blob expires — you can see the version history even if the raw file is gone.
        </Callout>

        <H2>Verifying storage via the API</H2>
        <CodeBlock lang="bash">{`# Check your current workspace usage
curl https://instadash.io/api/workspace \\
  -H "Authorization: Bearer sk_live_…"
# → { "storage_used_bytes": 1234567, "storage_limit_bytes": 107374182400, … }`}</CodeBlock>
      </>
    )
  }

  if (id === 'system-prompt') {
    return (
      <>
        <H2>Two LLM-readable endpoints</H2>
        <P>Every public grid ships two plain-text surfaces designed to be pasted directly into a system prompt or fetched at agent startup. No API key required.</P>
        <CodeBlock lang="bash">{`# Plain text — 40 sample rows, column list, grid metadata
curl https://instadash.io/<handle>/<slug>/llms.txt

# Markdown table — same data, human-readable, attribution footer
curl https://instadash.io/<handle>/<slug>/llms.md`}</CodeBlock>

        <H2>What llms.txt contains</H2>
        <CodeBlock lang="text">{`grid: raihan/sales-q2
title: Q2 2026 Revenue
description: Consolidated revenue across all regions
rows: 12408  cols: 8
columns: id, region, product, amount, currency, date, rep, status

--- sample rows (40 of 12408) ---
{"id":1,"region":"APAC","product":"Pro","amount":4200,"currency":"USD",…}
{"id":2,"region":"EMEA","product":"Builder","amount":1100,"currency":"EUR",…}
…`}</CodeBlock>

        <H2>What llms.md contains</H2>
        <CodeBlock lang="text">{`# raihan/sales-q2 · Q2 2026 Revenue

| id | region | product | amount | currency | date       | rep    | status |
|----|--------|---------|--------|----------|------------|--------|--------|
| 1  | APAC   | Pro     | 4200   | USD      | 2026-04-01 | Alice  | closed |
| 2  | EMEA   | Builder | 1100   | EUR      | 2026-04-02 | Bob    | open   |
…

---
attribution: { "v": 42, "source": "cli", "timestamp": "2026-05-07T09:14:22Z", "sha256": "e3b0c44…" }`}</CodeBlock>

        <H2>Paste into a system prompt</H2>
        <P>Fetch the <code>/llms.md</code> output and paste it into your system prompt before your first user message. The model will use the sample rows to ground its answers and cite the version for traceability.</P>
        <CodeBlock lang="text">{`You are an analyst with access to the following dataset:

<dataset>
{paste llms.md output here}
</dataset>

Answer questions about this data. Always cite the version number when referencing figures.`}</CodeBlock>

        <H2>Fetch at agent startup</H2>
        <P>For agents that run continuously, fetch the <code>/llms.txt</code> surface at startup rather than hardcoding it — so the agent always sees the latest version.</P>
        <CodeBlock lang="js">{`const res = await fetch('https://instadash.io/raihan/sales-q2/llms.txt')
const context = await res.text()
// Include \`context\` in your system prompt`}</CodeBlock>
      </>
    )
  }

  if (id === 'mesh-memory') {
    return (
      <>
        <H2>The push → query loop</H2>
        <P>Any public grid becomes a searchable memory cell. An agent can push data from one session, then query it in a completely different session — or a completely different agent — using the <code>instamesh_search</code> MCP tool.</P>
        <CodeBlock lang="text">{`Session A → instadash_push("research-notes", rows)
              ↓ registered on mesh (--public flag)
Session B → instamesh_search("research notes on topic X")
              ↓ returns citations with handle, slug, row sample, attribution`}</CodeBlock>

        <H2>instamesh_search via MCP</H2>
        <P>Once the MCP server is connected, your agent has <code>instamesh_search</code> as a native tool. It hits <code>/api/mesh/search</code> and returns a ranked list of matching grids.</P>
        <CodeBlock lang="json">{`// Tool call
{ "tool": "instamesh_search", "input": { "q": "AU housing prices 2026", "k": 5 } }

// Response
{
  "results": [
    {
      "handle": "abs",
      "slug":   "housing-2026-q1",
      "title":  "ABS Housing Index Q1 2026",
      "score":  0.91,
      "sample": [{"suburb":"Parramatta","median":920000}, …],
      "attribution": { "v": 7, "source": "api", "timestamp": "…" }
    }
  ]
}`}</CodeBlock>

        <H2>Direct HTTP search</H2>
        <P>You don't need MCP — any HTTP client can query the mesh directly.</P>
        <CodeBlock lang="bash">{`curl "https://instadash.io/api/mesh/search?q=AU+housing+prices&k=5"`}</CodeBlock>

        <H2>Designing for mesh memory</H2>
        <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 0, marginTop: 14, marginBottom: 20 }}>
          {[
            ['Descriptive names',  'Use --title and --description to make grids searchable by topic, not just slug.'],
            ['Structured rows',    'Flat JSON with consistent keys retrieves better than nested blobs.'],
            ['Frequent pushes',    'Push on every meaningful update — the mesh always returns the latest version.'],
            ['Tag consistently',   'Tags are indexed. Use --tags to group related grids (e.g. "finance,q2,2026").'],
          ].map(([k, v]) => (
            <li key={k} className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
              <span className="mono" style={{ fontSize: 11, color: 'var(--accent)', width: 140, paddingTop: 3, flexShrink: 0 }}>{k}</span>
              <span style={{ fontSize: 14, color: 'var(--fg-1)', lineHeight: 1.6 }}>{v}</span>
            </li>
          ))}
        </ul>
        <Callout tag="note">Private grids are never registered on the mesh. Only grids pushed with <code>--public</code> are searchable.</Callout>
      </>
    )
  }

  if (id === 'mag') {
    return (
      <>
        <H2>The problem with RAG</H2>
        <P>Retrieval-Augmented Generation chunks static documents, embeds them, and retrieves by cosine similarity. It works well for text. It breaks for structured, changing data — because the chunks go stale and lose context the moment the underlying data changes.</P>

        <H2>What MAG does differently</H2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 12, marginBottom: 24 }}>
          {[
            { label: 'RAG', items: ['Static document chunks', 'Embedding vectors age out', 'No provenance per row', 'Retrieves nearest text', 'Good for: docs, PDFs, notes'] },
            { label: 'MAG', items: ['Live structured grids', 'Always the latest push', 'SHA-256 + version per row', 'Retrieves by schema + query', 'Good for: data, metrics, logs'] },
          ].map(col => (
            <div key={col.label} className="card" style={{ padding: 18 }}>
              <div className="mono" style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 12, fontWeight: 600 }}>{col.label}</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {col.items.map(item => (
                  <li key={item} style={{ fontSize: 13, color: 'var(--fg-1)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ color: 'var(--fg-3)', flexShrink: 0, marginTop: 2 }}>·</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <H2>A concrete example</H2>
        <P>Your agent is asked: "What was our APAC revenue in Q2?" With RAG, it searches a PDF that was last updated three months ago. With MAG, it searches the mesh for the latest <code>sales-q2</code> grid, gets a sample of the actual rows, sees the version and timestamp, and answers with a citation.</P>
        <CodeBlock lang="text">{`// MAG answer:
"APAC Q2 revenue was $4.2M across 1,240 transactions.
Source: raihan/sales-q2 · v42 · pushed 2026-05-07 via cli · sha256 e3b0c4…"`}</CodeBlock>

        <H2>Combining MAG and RAG</H2>
        <P>They're not mutually exclusive. Use RAG for unstructured docs — design specs, meeting notes, RFCs. Use MAG for anything tabular, metered, or regularly updated. The mesh search result includes a schema so the model knows which columns to reference.</P>
        <Callout tag="tip">
          Name grids after the question they answer, not after the source file. <code>q2-apac-revenue</code> retrieves better than <code>export-2026-05.jsonl</code>.
        </Callout>
      </>
    )
  }

  // Generic fallback
  return (
    <>
      <H2>Overview</H2>
      <P>This is a placeholder for the <strong>{id}</strong> article. The full guide is in progress. In the meantime, the <code>quickstart</code>, <code>mcp</code>, and <code>mesh-protocol</code> articles cover most of the same surface.</P>
      <CodeBlock lang="bash">{`# Most things look like this:
echo $DATA | npx instadash push --key sk_… --name some-grid`}</CodeBlock>
      <Callout tag="todo">Stub article — full guide pending.</Callout>
    </>
  )
}
